'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Search,
  Building2,
  AlertTriangle,
} from 'lucide-react';
import type { SignupFormData } from './signup-wizard';

interface StepCharityInfoProps {
  form: UseFormReturn<SignupFormData>;
  isLoading: boolean;
}

// --- Charity Commission search types ---

interface CharityResult {
  charityNumber: number;
  name: string;
  charityType: string;
  dateOfRegistration: string;
}

interface CharityLookupResult {
  found: boolean;
  charity?: CharityResult & {
    regStatus: string;
    address: string;
    email: string | null;
    web: string | null;
    latestIncome: number | null;
    cioInd: boolean;
  };
  warnings?: string[];
}

// --- Subdomain derivation ---

const CHARITY_SUFFIXES = [
  'trust',
  'charity',
  'foundation',
  'cio',
  'association',
  'society',
  'fund',
  'group',
  'organisation',
  'organization',
  'limited',
  'ltd',
  'inc',
  'the',
];

function deriveSubdomain(charityName: string): string {
  let name = charityName.toLowerCase().trim();

  let changed = true;
  while (changed) {
    changed = false;
    for (const suffix of CHARITY_SUFFIXES) {
      const pattern = new RegExp(`\\s+${suffix}$`, 'i');
      if (pattern.test(name)) {
        name = name.replace(pattern, '').trim();
        changed = true;
      }
    }
  }

  name = name.replace(/^the\s+/i, '');
  const subdomain = name.replace(/[^a-z0-9]/g, '');
  return subdomain.slice(0, 30);
}

// --- Subdomain availability ---

type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

export function StepCharityInfo({ form, isLoading }: StepCharityInfoProps) {
  // Subdomain availability state
  const [availability, setAvailability] = useState<AvailabilityStatus>('idle');
  const [availabilityReason, setAvailabilityReason] = useState('');
  const lastSuggestionRef = useRef('');
  const subdomainDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const subdomainAbortRef = useRef<AbortController>(undefined);

  // Charity Commission search state
  const [searchResults, setSearchResults] = useState<CharityResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [verifiedCharity, setVerifiedCharity] = useState<string | null>(null);
  const [charityWarnings, setCharityWarnings] = useState<string[]>([]);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const searchAbortRef = useRef<AbortController>(undefined);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // --- Subdomain availability checking ---

  const checkAvailability = useCallback(async (subdomain: string) => {
    subdomainAbortRef.current?.abort();

    if (!subdomain || subdomain.length < 3) {
      setAvailability('idle');
      setAvailabilityReason('');
      return;
    }

    if (!/^[a-z0-9]{3,30}$/.test(subdomain)) {
      setAvailability('invalid');
      setAvailabilityReason('Must be 3-30 lowercase letters and numbers');
      return;
    }

    setAvailability('checking');
    const controller = new AbortController();
    subdomainAbortRef.current = controller;

    try {
      const res = await fetch(
        `/api/register/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`,
        { signal: controller.signal }
      );
      const data = await res.json();
      if (controller.signal.aborted) return;

      if (data.available) {
        setAvailability('available');
        setAvailabilityReason('');
      } else {
        setAvailability('taken');
        setAvailabilityReason(data.reason || 'This subdomain is already taken');
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setAvailability('idle');
    }
  }, []);

  const debouncedSubdomainCheck = useCallback(
    (subdomain: string) => {
      clearTimeout(subdomainDebounceRef.current);
      subdomainDebounceRef.current = setTimeout(
        () => checkAvailability(subdomain),
        500
      );
    },
    [checkAvailability]
  );

  // --- Charity Commission name search ---

  const searchCharities = useCallback(async (query: string) => {
    searchAbortRef.current?.abort();

    if (query.length < 3) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    const controller = new AbortController();
    searchAbortRef.current = controller;

    try {
      const res = await fetch(
        `/api/register/charity-search?q=${encodeURIComponent(query)}`,
        { signal: controller.signal }
      );
      const data = await res.json();
      if (controller.signal.aborted) return;

      if (data.charities?.length > 0) {
        setSearchResults(data.charities);
        setShowDropdown(true);
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      // Silently fail — the search is optional, manual entry still works
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      if (!controller.signal.aborted) setIsSearching(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    (query: string) => {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => searchCharities(query), 400);
    },
    [searchCharities]
  );

  // --- Charity number lookup ---

  const lookupCharity = useCallback(
    async (charityNumber: string) => {
      if (!/^\d{1,7}$/.test(charityNumber)) return;

      setLookupError(null);
      setIsSearching(true);

      try {
        const res = await fetch(
          `/api/register/charity-lookup?number=${encodeURIComponent(charityNumber)}`
        );
        const data: CharityLookupResult = await res.json();

        if (data.found && data.charity) {
          // Auto-populate charity name from the register
          form.setValue('charityName', data.charity.name, { shouldValidate: true });
          setVerifiedCharity(data.charity.name);
          setCharityWarnings(data.warnings || []);

          // Auto-suggest subdomain from the verified name
          const suggested = deriveSubdomain(data.charity.name);
          const currentSubdomain = form.getValues('subdomain');
          if (
            suggested.length >= 3 &&
            (!currentSubdomain || currentSubdomain === lastSuggestionRef.current)
          ) {
            form.setValue('subdomain', suggested, { shouldValidate: true });
            lastSuggestionRef.current = suggested;
            debouncedSubdomainCheck(suggested);
          }
        } else {
          setLookupError('No charity found with this number');
          setVerifiedCharity(null);
          setCharityWarnings([]);
        }
      } catch {
        // Silently fail — lookup is helpful but not required
        setLookupError(null);
      } finally {
        setIsSearching(false);
      }
    },
    [form, debouncedSubdomainCheck]
  );

  // --- Select a charity from search results ---

  const selectCharity = useCallback(
    (charity: CharityResult) => {
      form.setValue('charityName', charity.name, { shouldValidate: true });
      form.setValue('charityNumber', String(charity.charityNumber), {
        shouldValidate: true,
      });
      setVerifiedCharity(charity.name);
      setShowDropdown(false);
      setSearchResults([]);
      setCharityWarnings([]);
      setLookupError(null);

      // Auto-suggest subdomain
      const suggested = deriveSubdomain(charity.name);
      const currentSubdomain = form.getValues('subdomain');
      if (
        suggested.length >= 3 &&
        (!currentSubdomain || currentSubdomain === lastSuggestionRef.current)
      ) {
        form.setValue('subdomain', suggested, { shouldValidate: true });
        lastSuggestionRef.current = suggested;
        debouncedSubdomainCheck(suggested);
      }

      // Also look up full details for warnings
      lookupCharity(String(charity.charityNumber));
    },
    [form, debouncedSubdomainCheck, lookupCharity]
  );

  // --- Close dropdown on outside click ---

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearTimeout(subdomainDebounceRef.current);
      clearTimeout(searchDebounceRef.current);
      subdomainAbortRef.current?.abort();
      searchAbortRef.current?.abort();
    };
  }, []);

  // --- Handlers ---

  const handleCharityNameChange = (
    value: string,
    fieldOnChange: (value: string) => void
  ) => {
    fieldOnChange(value);
    // Clear verified state when name is manually changed
    if (verifiedCharity && value !== verifiedCharity) {
      setVerifiedCharity(null);
      setCharityWarnings([]);
    }
    debouncedSearch(value);
  };

  const handleCharityNameBlur = () => {
    const charityName = form.getValues('charityName');
    const currentSubdomain = form.getValues('subdomain');

    if (!charityName) return;

    const suggested = deriveSubdomain(charityName);
    if (!suggested || suggested.length < 3) return;

    if (!currentSubdomain || currentSubdomain === lastSuggestionRef.current) {
      form.setValue('subdomain', suggested, { shouldValidate: true });
      lastSuggestionRef.current = suggested;
      debouncedSubdomainCheck(suggested);
    }
  };

  const handleCharityNumberBlur = () => {
    const num = form.getValues('charityNumber');
    if (num && /^\d{1,7}$/.test(num)) {
      lookupCharity(num);
    }
  };

  const handleSubdomainChange = (
    value: string,
    fieldOnChange: (value: string) => void
  ) => {
    const normalised = value.toLowerCase().replace(/[^a-z0-9]/g, '');
    fieldOnChange(normalised);
    debouncedSubdomainCheck(normalised);
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Your Charity</h2>
        <p className="text-sm text-gray-500">
          Search the Charity Commission register or enter your details manually
        </p>
      </div>

      {/* Charity Name with search */}
      <FormField
        control={form.control}
        name="charityName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Charity Name</FormLabel>
            <FormControl>
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Input
                    placeholder="Start typing to search the register..."
                    {...field}
                    onChange={(e) =>
                      handleCharityNameChange(e.target.value, field.onChange)
                    }
                    onBlur={() => {
                      field.onBlur();
                      // Delay closing dropdown so click on result registers
                      setTimeout(() => setShowDropdown(false), 200);
                      handleCharityNameBlur();
                    }}
                    onFocus={() => {
                      if (searchResults.length > 0) setShowDropdown(true);
                    }}
                    disabled={isLoading}
                    className="pr-9"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2">
                    {isSearching ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    ) : verifiedCharity ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Search className="h-4 w-4 text-gray-400" />
                    )}
                  </span>
                </div>

                {/* Search results dropdown */}
                {showDropdown && searchResults.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg max-h-60 overflow-auto">
                    {searchResults.map((charity) => (
                      <button
                        key={charity.charityNumber}
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-start gap-2"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blur before click
                          selectCharity(charity);
                        }}
                      >
                        <Building2 className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-sm font-medium">
                            {charity.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            No. {charity.charityNumber} &middot;{' '}
                            {charity.charityType}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </FormControl>
            {verifiedCharity && (
              <p className="text-sm text-green-600">
                Verified on the Charity Commission register
              </p>
            )}
            <FormDescription>
              Search by name to find your charity on the register, or enter
              manually if not registered with CCEW
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Charity Number */}
      <FormField
        control={form.control}
        name="charityNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Charity Registration Number{' '}
              <span className="text-gray-400 font-normal">(optional)</span>
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  placeholder="e.g. 1234567"
                  {...field}
                  onBlur={() => {
                    field.onBlur();
                    handleCharityNumberBlur();
                  }}
                  disabled={isLoading}
                  className="pr-9"
                />
                {isSearching && field.value && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </span>
                )}
              </div>
            </FormControl>
            {lookupError && (
              <p className="text-sm text-amber-600">{lookupError}</p>
            )}
            <FormDescription>
              Enter your CCEW number to auto-fill charity details, or leave blank
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Warnings from Charity Commission */}
      {charityWarnings.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc pl-4">
              {charityWarnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Subdomain */}
      <FormField
        control={form.control}
        name="subdomain"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Subdomain</FormLabel>
            <FormControl>
              <div className="flex items-center">
                <div className="relative flex-1">
                  <Input
                    placeholder="yourcharity"
                    {...field}
                    onChange={(e) =>
                      handleSubdomainChange(e.target.value, field.onChange)
                    }
                    onBlur={(e) => {
                      field.onBlur();
                      checkAvailability(e.target.value);
                    }}
                    disabled={isLoading}
                    className="rounded-r-none pr-9"
                  />
                  {availability !== 'idle' && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2">
                      {availability === 'checking' && (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      )}
                      {availability === 'available' && (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      )}
                      {(availability === 'taken' || availability === 'invalid') && (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                    </span>
                  )}
                </div>
                <span className="bg-gray-100 border border-l-0 rounded-r px-3 py-2 text-sm text-gray-600">
                  .trusteekit.co.uk
                </span>
              </div>
            </FormControl>
            {availability === 'available' && (
              <p className="text-sm text-green-600">This subdomain is available</p>
            )}
            {availability === 'taken' && (
              <p className="text-sm text-red-500">{availabilityReason}</p>
            )}
            {availability === 'invalid' && (
              <p className="text-sm text-red-500">{availabilityReason}</p>
            )}
            {availability !== 'taken' && availability !== 'invalid' && (
              <FormDescription>
                This will be your donation page URL. Use only lowercase letters
                and numbers.
              </FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
